<section class="eb">
	<div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
		<div class="min-w-0">
			<h5 class="mb-1">Livro eletrônico</h5>
			<p class="eb-footnote mb-0">
				Todos os documentos e anexos num PDF único, na ordem do processo e com o carimbo de folha. Os arquivos ficam disponíveis por 72 horas; o
				histórico fica.
			</p>
		</div>
		<button
			*permissions="'planning.electronic-process.read'"
			type="button"
			class="btn btn-primary fw-600 text-nowrap"
			[disabled]="requesting || !!active || !processId"
			(click)="request()">
			<fa-icon icon="book-open" class="me-1"></fa-icon>
			{{ requesting ? 'Solicitando...' : books.length ? 'Gerar novamente' : 'Gerar livro' }}
		</button>
	</div>

	@if (active; as book) {
		<div class="eb-highlight" data-tone="info">
			<div class="d-flex flex-wrap align-items-center gap-3">
				<span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
				<div class="flex-grow-1 min-w-0">
					<div class="fw-600">{{ book.progressLabel }}</div>
					<div class="eb-footnote">
						Pedido {{ book.createdAt | date: "dd/MM/yyyy 'às' HH:mm" }}
						@if (book.createdBy && authors[book.createdBy]) {
							por {{ authors[book.createdBy] }}
						}
						· você pode sair desta tela; quem pediu recebe um aviso quando terminar.
					</div>
				</div>
				<span class="eb-percent">{{ book.percent }}%</span>
			</div>
			<ngb-progressbar class="mt-2" type="info" height=".4rem" [value]="book.percent || 2" [animated]="true" [striped]="true"></ngb-progressbar>
		</div>
	} @else if (latestAvailable; as book) {
		<div class="eb-highlight" data-tone="success">
			<div class="d-flex flex-wrap align-items-center gap-3">
				<fa-icon icon="circle-check" class="eb-highlight-icon"></fa-icon>
				<div class="flex-grow-1 min-w-0">
					<div class="fw-600">Livro disponível até {{ book.expiresAt | date: "dd/MM/yyyy 'às' HH:mm" }}</div>
					<div class="eb-footnote">
						{{ book.totalPages ?? 0 | number }} folhas
						@if (book.sizeLabel) {
							· {{ book.sizeLabel }}
						}
					</div>
				</div>
				<button type="button" class="btn btn-sm btn-outline-primary fw-600" (click)="open(book)">
					<fa-icon icon="eye" class="me-1"></fa-icon>
					Visualizar
				</button>
				<button type="button" class="btn btn-sm btn-primary fw-600" [disabled]="downloadingId === book.id" (click)="download(book)">
					<fa-icon icon="download" class="me-1"></fa-icon>
					{{ downloadingId === book.id ? 'Preparando...' : 'Baixar' }}
				</button>
			</div>
		</div>
	}

	<div class="d-flex justify-content-between align-items-baseline mt-4 mb-2">
		<h6 class="eb-section-title mb-0">Histórico de gerações</h6>
		@if (totalElements) {
			<span class="eb-footnote">{{ totalElements | number }} {{ totalElements === 1 ? 'geração' : 'gerações' }}</span>
		}
	</div>

	@if (loading && !books.length) {
		<app-pre-loading msg="Carregando o histórico do livro"></app-pre-loading>
	} @else if (!books.length) {
		<app-empty-data message="Nenhum livro gerado para este processo ainda."></app-empty-data>
	} @else {
		<div class="eb-list">
			@for (book of books; track book.id) {
				<div class="eb-row" [attr.data-tone]="book.tone">
					<div class="d-flex flex-wrap flex-md-nowrap justify-content-between align-items-start gap-3">
						<div class="flex-grow-1 min-w-0">
							<div class="d-flex align-items-center flex-wrap gap-2 mb-1">
								<span class="fw-600">Geração de {{ book.createdAt | date: "dd/MM/yyyy 'às' HH:mm" }}</span>
								<span class="eb-pill" [attr.data-tone]="book.tone">
									@if (book.active) {
										<span class="spinner-border spinner-border-sm me-1" style="width: 0.65rem; height: 0.65rem" aria-hidden="true"></span>
									}
									{{ book.active ? book.progressLabel : book.statusLabel }}
								</span>
							</div>

							<div class="eb-figures">
								@if (book.createdBy && authors[book.createdBy]) {
									Pedido por {{ authors[book.createdBy] }}
								}
								@if (book.ready) {
									@if (book.createdBy && authors[book.createdBy]) {
										·
									}
									<span class="eb-figures-strong">{{ book.totalPages ?? 0 | number }}</span>
									folhas
									@if (book.sizeLabel) {
										· {{ book.sizeLabel }}
									}
									·
									@if (book.available) {
										disponível até {{ book.expiresAt | date: "dd/MM/yyyy 'às' HH:mm" }}
									} @else {
										arquivos expiraram em {{ book.expiresAt | date: "dd/MM/yyyy 'às' HH:mm" }}
									}
								}
							</div>

							@if (book.status === Status.UNCHANGED) {
								<div class="eb-figures">
									Nada mudou desde a geração
									@if (reusedFrom(book); as previous) {
										de {{ previous.createdAt | date: "dd/MM/yyyy 'às' HH:mm" }}
									} @else {
										anterior
									}
									: o mesmo livro, com prazo novo.
								</div>
							}

							@if (book.failed) {
								<p class="eb-error mb-0">{{ book.error?.message || 'Não foi possível gerar o livro.' }}</p>
								@if (book.failures.length) {
									<button type="button" class="btn btn-link btn-sm px-0" (click)="toggle(book)" [attr.aria-expanded]="expanded.has(book.id)">
										<fa-icon [icon]="expanded.has(book.id) ? 'chevron-down' : 'chevron-right'" class="me-1"></fa-icon>
										{{ expanded.has(book.id) ? 'Ocultar documentos' : 'Ver documentos com problema' }}
									</button>
								}
								@if (expanded.has(book.id)) {
									<ul class="eb-failures">
										@for (unit of book.failures; track $index) {
											<li>
												<span class="fw-600">{{ unit.label || 'Arquivo ' + unit.seq }}</span>
												<span class="text-muted">· {{ reason(unit) }}</span>
											</li>
										}
									</ul>
									@if (paginationIssue(book)) {
										<p class="eb-hint mb-0">
											A paginação gravada não confere com os arquivos. Use "Reprocessar paginação" no processo e gere o livro de novo.
										</p>
									}
								}
							}
						</div>

						<div class="flex-shrink-0 d-flex align-items-center gap-2">
							@if (book.available) {
								<button type="button" class="btn btn-sm btn-outline-primary" (click)="open(book)">Visualizar</button>
								<button type="button" class="btn btn-sm btn-outline-primary" [disabled]="downloadingId === book.id" (click)="download(book)">
									{{ downloadingId === book.id ? 'Preparando...' : 'Baixar' }}
								</button>
							} @else if (book.ready && !active) {
								<button
									*permissions="'planning.electronic-process.read'"
									type="button"
									class="btn btn-sm btn-link"
									[disabled]="requesting"
									(click)="request()">
									Gerar novamente
								</button>
							}
						</div>
					</div>
				</div>
			}
		</div>

		@if (totalElements > pageSize) {
			<div class="d-flex justify-content-end pt-3">
				<ngb-pagination
					[rotate]="true"
					[maxSize]="5"
					[pageSize]="pageSize"
					[page]="page + 1"
					[collectionSize]="totalElements"
					(pageChange)="onPageChange($event - 1)"></ngb-pagination>
			</div>
		}
	}
</section>
